import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import { WorkspaceService } from '@affine/core/modules/workspace/services/workspace';
import { workspacePermissionCacheOwnerKey } from '@affine/core/modules/workspace/utils/permission-cache-identity';
import {
  isBackendKnownUnavailable,
  onBackendAvailable,
} from '@affine/core/utils/backend-availability';
import { LiveData, Service } from '@toeverything/infra';
import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';
import { nanoid } from 'nanoid';
import { Subject } from 'rxjs';

import {
  type OfflineMutationExecutor,
  type OfflineMutationInput,
  type OfflineMutationOperation,
  type OfflineMutationOutboxStorage,
  type OfflineMutationRecord,
  type OfflineMutationScope,
  offlineMutationScopeKey,
} from '../types';

const DB_NAME = 'streamlain-offline-mutation-outbox';
const STORE_NAME = 'mutations';
const DEFAULT_MAX_RETRY_ATTEMPTS = 10;
const DEFAULT_MAX_RETRY_DELAY_MS = 5 * 60 * 1000;

interface OfflineMutationOutboxDB extends DBSchema {
  mutations: {
    key: string;
    value: OfflineMutationRecord;
    indexes: {
      scopeKey: string;
      idempotencyKey: string;
    };
  };
}

export class OfflineMutationTransientError extends Error {
  readonly kind = 'transient';
}

export class OfflineMutationDomainError extends Error {
  readonly kind = 'domain';
}

export interface OfflineMutationOutboxServiceOptions {
  storage?: OfflineMutationOutboxStorage;
  autoFlush?: boolean;
  now?: () => number;
  createId?: () => string;
  maxRetryAttempts?: number;
  maxRetryDelayMs?: number;
  retryDelayMs?: number;
}

export class MemoryOfflineMutationOutboxStorage implements OfflineMutationOutboxStorage {
  private readonly records = new Map<string, OfflineMutationRecord>();

  async listPending(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(
        record =>
          record.scopeKey === scopeKey &&
          (record.status === 'pending' || record.status === 'flushing')
      )
      .sort(compareMutations);
  }

  async listFailed(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(
        record => record.scopeKey === scopeKey && record.status === 'failed'
      )
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]> {
    const scopeKey = offlineMutationScopeKey(scope);
    return [...this.records.values()]
      .filter(record => record.scopeKey === scopeKey)
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async insertPendingIfAbsent(
    record: OfflineMutationRecord
  ): Promise<{ record: OfflineMutationRecord; inserted: boolean }> {
    const existing = [...this.records.values()].find(candidate =>
      isPendingDuplicate(candidate, record)
    );
    if (existing) {
      return { record: cloneMutation(existing), inserted: false };
    }
    this.records.set(record.id, cloneMutation(record));
    return { record: cloneMutation(record), inserted: true };
  }

  async claim(id: string, now: number): Promise<OfflineMutationRecord | null> {
    const record = this.records.get(id);
    const claimed = record ? claimedMutation(record, now) : null;
    if (claimed && claimed !== record) {
      this.records.set(id, cloneMutation(claimed));
    }
    return claimed ? cloneMutation(claimed) : null;
  }

  async removeIfUnsent(id: string): Promise<boolean> {
    const record = this.records.get(id);
    if (!record || !isUnsentMutation(record)) {
      return false;
    }
    this.records.delete(id);
    return true;
  }

  async upsert(mutation: OfflineMutationRecord): Promise<void> {
    this.records.set(mutation.id, cloneMutation(mutation));
  }

  async update(mutation: OfflineMutationRecord): Promise<void> {
    await this.upsert(mutation);
  }

  async remove(id: string): Promise<void> {
    this.records.delete(id);
  }

  async clearScope(scope: OfflineMutationScope): Promise<void> {
    const scopeKey = offlineMutationScopeKey(scope);
    for (const record of this.records.values()) {
      if (record.scopeKey === scopeKey) {
        this.records.delete(record.id);
      }
    }
  }

  debugRecords(): OfflineMutationRecord[] {
    return [...this.records.values()].map(cloneMutation).sort(compareMutations);
  }
}

export class IndexedDBOfflineMutationOutboxStorage implements OfflineMutationOutboxStorage {
  private dbPromise: Promise<IDBPDatabase<OfflineMutationOutboxDB>> | null =
    null;

  private get db(): Promise<IDBPDatabase<OfflineMutationOutboxDB>> {
    this.dbPromise ??= openDB<OfflineMutationOutboxDB>(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
          });
          store.createIndex('scopeKey', 'scopeKey');
          store.createIndex('idempotencyKey', 'idempotencyKey');
        }
      },
    });
    return this.dbPromise;
  }

  async listPending(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records
      .filter(
        record => record.status === 'pending' || record.status === 'flushing'
      )
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listFailed(
    scope: OfflineMutationScope
  ): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records
      .filter(record => record.status === 'failed')
      .map(cloneMutation)
      .sort(compareMutations);
  }

  async listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    return records.map(cloneMutation).sort(compareMutations);
  }

  async insertPendingIfAbsent(
    record: OfflineMutationRecord
  ): Promise<{ record: OfflineMutationRecord; inserted: boolean }> {
    const db = await this.db;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const candidates = await tx.store
      .index('idempotencyKey')
      .getAll(record.idempotencyKey);
    const existing = candidates.find(candidate =>
      isPendingDuplicate(candidate, record)
    );
    if (!existing) {
      await tx.store.put(cloneMutation(record));
    }
    await tx.done;
    return existing
      ? { record: cloneMutation(existing), inserted: false }
      : { record: cloneMutation(record), inserted: true };
  }

  async claim(id: string, now: number): Promise<OfflineMutationRecord | null> {
    const db = await this.db;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const record = await tx.store.get(id);
    const claimed = record ? claimedMutation(record, now) : null;
    if (claimed && claimed !== record) {
      await tx.store.put(cloneMutation(claimed));
    }
    await tx.done;
    return claimed ? cloneMutation(claimed) : null;
  }

  async removeIfUnsent(id: string): Promise<boolean> {
    const db = await this.db;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const record = await tx.store.get(id);
    const removable = record !== undefined && isUnsentMutation(record);
    if (removable) {
      await tx.store.delete(id);
    }
    await tx.done;
    return removable;
  }

  async upsert(mutation: OfflineMutationRecord): Promise<void> {
    const db = await this.db;
    await db.put(STORE_NAME, cloneMutation(mutation));
  }

  async update(mutation: OfflineMutationRecord): Promise<void> {
    await this.upsert(mutation);
  }

  async remove(id: string): Promise<void> {
    const db = await this.db;
    await db.delete(STORE_NAME, id);
  }

  async clearScope(scope: OfflineMutationScope): Promise<void> {
    const db = await this.db;
    const scopeKey = offlineMutationScopeKey(scope);
    const records = await db.getAllFromIndex(STORE_NAME, 'scopeKey', scopeKey);
    const tx = db.transaction(STORE_NAME, 'readwrite');
    for (const record of records) {
      await tx.store.delete(record.id);
    }
    await tx.done;
  }

  async close(): Promise<void> {
    if (!this.dbPromise) {
      return;
    }
    const db = await this.dbPromise;
    db.close();
    this.dbPromise = null;
  }
}

export class OfflineMutationOutboxService extends Service {
  readonly failed$ = new Subject<OfflineMutationRecord>();
  readonly completed$ = new Subject<OfflineMutationRecord>();
  readonly failedMutations$ = new LiveData<OfflineMutationRecord[]>([]);
  readonly pendingMutations$ = new LiveData<OfflineMutationRecord[]>([]);

  private readonly storage: OfflineMutationOutboxStorage;
  private readonly autoFlush: boolean;
  private readonly now: () => number;
  private readonly createId: () => string;
  private readonly maxRetryAttempts: number;
  private readonly maxRetryDelayMs: number;
  private readonly retryDelayMs: number;
  private sequenceCounter = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimerDueAt: number | null = null;
  private readonly executors = new Map<
    OfflineMutationOperation,
    OfflineMutationExecutor
  >();
  private readonly flushes = new Map<string, Promise<void>>();
  private readonly canceledMutationIds = new Set<string>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    options: OfflineMutationOutboxServiceOptions = {}
  ) {
    super();
    this.storage =
      options.storage ?? new IndexedDBOfflineMutationOutboxStorage();
    this.autoFlush = options.autoFlush ?? true;
    this.now = options.now ?? Date.now;
    this.createId = options.createId ?? nanoid;
    this.maxRetryAttempts = Math.max(
      1,
      options.maxRetryAttempts ?? DEFAULT_MAX_RETRY_ATTEMPTS
    );
    this.maxRetryDelayMs = Math.max(
      0,
      options.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS
    );
    this.retryDelayMs = Math.max(0, options.retryDelayMs ?? 1000);
    this.registerFlushTriggers();
    this.refreshMutationViews().catch(() => {});
    this.disposables.push(() => {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
        this.retryTimerDueAt = null;
      }
    });
  }

  registerExecutor<Operation extends OfflineMutationOperation>(
    operation: Operation,
    executor: OfflineMutationExecutor<Operation>
  ): () => void {
    this.executors.set(operation, executor as OfflineMutationExecutor);
    this.scheduleFlush();
    return () => {
      if (this.executors.get(operation) === executor) {
        this.executors.delete(operation);
      }
    };
  }

  async enqueue<Operation extends OfflineMutationOperation>(
    input: OfflineMutationInput<Operation>
  ): Promise<OfflineMutationRecord<Operation>> {
    const scope = this.requireScope();
    const now = this.now();
    const holdMs = input.holdMs ?? 0;
    const mutation: OfflineMutationRecord<Operation> = {
      id:
        input.id ??
        (input.dedupePending
          ? dedupedMutationId(scope, input.idempotencyKey)
          : this.createId()),
      workspaceId: scope.workspaceId,
      cacheOwnerKey: scope.cacheOwnerKey,
      scopeKey: offlineMutationScopeKey(scope),
      operation: input.operation,
      payload: input.payload,
      idempotencyKey: input.idempotencyKey,
      status: 'pending',
      attempts: 0,
      createdAt: now,
      sequence: ++this.sequenceCounter,
      updatedAt: now,
      ...(holdMs > 0 && { claimableAt: now + holdMs }),
      ...(input.orderKey !== undefined && { orderKey: input.orderKey }),
    };
    if (input.dedupePending) {
      const { record, inserted } =
        await this.storage.insertPendingIfAbsent(mutation);
      if (!inserted) {
        return record as OfflineMutationRecord<Operation>;
      }
    } else {
      await this.storage.upsert(mutation);
    }
    await this.refreshPendingMutations();
    this.scheduleFlush();
    return mutation;
  }

  /**
   * Removes a mutation that was never sent. True means it will never reach
   * its executor; false means it was already claimed, sent or failed, and
   * promises nothing.
   */
  async cancelPending(id: string): Promise<boolean> {
    const removed = await this.storage.removeIfUnsent(id);
    await this.refreshPendingMutations();
    return removed;
  }

  async flush(forceAfterReconnect = false): Promise<void> {
    const scope = this.requireScope();
    const scopeKey = offlineMutationScopeKey(scope);
    const existing = this.flushes.get(scopeKey);
    if (existing) {
      return existing;
    }

    const flush = this.flushScope(scope, forceAfterReconnect).finally(() => {
      if (this.flushes.get(scopeKey) === flush) {
        this.flushes.delete(scopeKey);
      }
    });
    this.flushes.set(scopeKey, flush);
    return flush;
  }

  async refreshFailedMutations(): Promise<OfflineMutationRecord[]> {
    const failed = await this.storage.listFailed(this.requireScope());
    this.failedMutations$.setValue(failed);
    return failed;
  }

  async refreshPendingMutations(): Promise<OfflineMutationRecord[]> {
    const pending = await this.storage.listPending(this.requireScope());
    this.pendingMutations$.setValue(pending);
    return pending;
  }

  async refreshMutationViews(): Promise<void> {
    await Promise.all([
      this.refreshPendingMutations(),
      this.refreshFailedMutations(),
    ]);
  }

  async retryFailed(id?: string): Promise<void> {
    const failed = await this.storage.listFailed(this.requireScope());
    const now = this.now();
    for (const mutation of failed) {
      if (id && mutation.id !== id) {
        continue;
      }
      await this.storage.update({
        ...mutation,
        status: 'pending',
        attempts: 0,
        updatedAt: now,
        lastError: undefined,
        failure: undefined,
      });
    }
    this.clearRetryTimer();
    await this.refreshMutationViews();
    this.scheduleFlush();
  }

  async removeFailed(id: string): Promise<boolean> {
    const failed = await this.storage.listFailed(this.requireScope());
    const mutation = failed.find(mutation => mutation.id === id);
    if (!mutation) {
      return false;
    }
    await this.storage.remove(mutation.id);
    await this.refreshFailedMutations();
    return true;
  }

  async removeFailedWhere(
    predicate: (mutation: OfflineMutationRecord) => boolean
  ): Promise<number> {
    const failed = await this.storage.listFailed(this.requireScope());
    let removed = 0;
    for (const mutation of failed) {
      if (!predicate(mutation)) continue;
      await this.storage.remove(mutation.id);
      removed += 1;
    }
    if (removed > 0) await this.refreshFailedMutations();
    return removed;
  }

  async removePendingWhere(
    predicate: (mutation: OfflineMutationRecord) => boolean
  ): Promise<number> {
    const pending = await this.storage.listPending(this.requireScope());
    let removed = 0;
    for (const mutation of pending) {
      if (!predicate(mutation)) {
        continue;
      }
      if (mutation.status === 'flushing') {
        this.canceledMutationIds.add(mutation.id);
      }
      await this.storage.remove(mutation.id);
      removed += 1;
    }
    if (removed > 0) {
      await this.refreshPendingMutations();
    }
    return removed;
  }

  private async flushScope(
    scope: OfflineMutationScope,
    forceAfterReconnect: boolean
  ): Promise<void> {
    if (!this.canAttemptMutation(forceAfterReconnect)) {
      return;
    }

    // The earliest hold seen; its timer is the shared retry timer.
    let nextClaimableAt: number | null = null;
    try {
      while (true) {
        const mutations = await this.storage.listPending(scope);
        if (mutations.length === 0) {
          return;
        }

        let processedKnownMutation = false;
        // Targets whose earlier mutation waits: their later ones wait too.
        const waitingOrderKeys = new Set<string>();
        const waitBehind = (mutation: OfflineMutationRecord) => {
          if (mutation.orderKey !== undefined) {
            waitingOrderKeys.add(mutation.orderKey);
          }
        };
        for (const mutation of mutations) {
          if (this.consumeCanceledMutation(mutation)) {
            await this.storage.remove(mutation.id);
            processedKnownMutation = true;
            continue;
          }

          if (
            mutation.orderKey !== undefined &&
            waitingOrderKeys.has(mutation.orderKey)
          ) {
            continue;
          }

          const executor = this.executors.get(mutation.operation);
          if (!executor) {
            waitBehind(mutation);
            continue;
          }

          // A held mutation is skipped without counting as processed, so the
          // loop ends instead of reading the same held list again.
          const now = this.now();
          if (
            mutation.claimableAt !== undefined &&
            mutation.claimableAt > now
          ) {
            nextClaimableAt = Math.min(
              nextClaimableAt ?? mutation.claimableAt,
              mutation.claimableAt
            );
            waitBehind(mutation);
            continue;
          }
          processedKnownMutation = true;

          const retryWaitMs = forceAfterReconnect
            ? 0
            : this.retryWaitMs(mutation);
          if (retryWaitMs > 0) {
            this.scheduleRetry(retryWaitMs);
            return;
          }

          // Only what the claim hands back is sent: a mutation canceled after
          // the list was read is gone and stays unsent.
          const claimed = await this.storage.claim(mutation.id, now);
          if (!claimed) {
            continue;
          }
          await this.refreshPendingMutations();

          try {
            await executor(claimed);
            if (this.consumeCanceledMutation(claimed)) {
              await this.storage.remove(claimed.id);
              await this.refreshPendingMutations();
              continue;
            }
            await this.storage.remove(claimed.id);
            await this.refreshPendingMutations();
            this.completed$.next(claimed);
          } catch (err) {
            if (this.consumeCanceledMutation(claimed)) {
              await this.storage.remove(claimed.id);
              await this.refreshPendingMutations();
              continue;
            }
            const updated = this.failedMutation(claimed, err);
            await this.storage.update(updated);
            await this.refreshMutationViews();
            if (updated.status === 'failed') {
              this.failed$.next(updated);
              continue;
            }
            this.scheduleRetry(this.retryDelayForAttempts(updated.attempts));
            return;
          }
        }

        if (!processedKnownMutation) {
          return;
        }
      }
    } finally {
      if (nextClaimableAt !== null) {
        this.scheduleRetry(nextClaimableAt - this.now());
      }
    }
  }

  private consumeCanceledMutation(mutation: OfflineMutationRecord): boolean {
    if (!this.canceledMutationIds.has(mutation.id)) {
      return false;
    }
    this.canceledMutationIds.delete(mutation.id);
    return true;
  }

  private failedMutation(
    mutation: OfflineMutationRecord,
    err: unknown
  ): OfflineMutationRecord {
    const lastError = errorMessage(err);
    const transient = isTransientMutationError(err);
    const attempts = mutation.attempts + 1;
    const retryExhausted = transient && attempts >= this.maxRetryAttempts;
    if (transient && !retryExhausted) {
      return {
        ...mutation,
        status: 'pending',
        attempts,
        updatedAt: this.now(),
        lastError,
      };
    }
    return {
      ...mutation,
      status: 'failed',
      attempts,
      updatedAt: this.now(),
      lastError,
      failure: transient ? 'retries-exhausted' : 'refused',
    };
  }

  private retryDelayForAttempts(attempts: number): number {
    const exponent = Math.min(Math.max(0, attempts - 1), 30);
    return Math.min(this.maxRetryDelayMs, this.retryDelayMs * 2 ** exponent);
  }

  private retryWaitMs(mutation: OfflineMutationRecord): number {
    if (mutation.attempts <= 0 || !mutation.lastError) {
      return 0;
    }
    const retryAt =
      mutation.updatedAt + this.retryDelayForAttempts(mutation.attempts);
    return Math.max(0, retryAt - this.now());
  }

  private requireScope(): OfflineMutationScope {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      throw new OfflineMutationDomainError(
        'Offline mutation outbox requires a cloud workspace'
      );
    }

    const server = this.workspaceServerService.server;
    const accountId = server?.account$?.value?.id;
    if (!server || !accountId) {
      throw new OfflineMutationTransientError(
        'No server account scope for offline mutation outbox'
      );
    }

    return {
      workspaceId: workspace.id,
      cacheOwnerKey: workspacePermissionCacheOwnerKey({
        accountId,
        serverBaseUrl: server.baseUrl,
        serverId: server.id,
      }),
    };
  }

  private scheduleFlush(forceAfterReconnect = false): void {
    if (!this.canAutoFlush(forceAfterReconnect)) {
      return;
    }
    queueMicrotask(() => {
      if (!this.canAutoFlush(forceAfterReconnect)) {
        return;
      }
      this.flush(forceAfterReconnect).catch(() => {});
    });
  }

  private scheduleRetry(delayMs: number): void {
    if (!this.canAutoFlush()) {
      return;
    }

    const dueAt = this.now() + Math.max(0, delayMs);
    if (
      this.retryTimer &&
      this.retryTimerDueAt !== null &&
      this.retryTimerDueAt <= dueAt
    ) {
      return;
    }
    if (this.retryTimer) {
      this.clearRetryTimer();
    }
    this.retryTimerDueAt = dueAt;

    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.retryTimerDueAt = null;
        if (!this.canAutoFlush()) {
          return;
        }
        this.flush().catch(() => {});
      },
      Math.max(0, delayMs)
    );
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    this.retryTimer = null;
    this.retryTimerDueAt = null;
  }

  private canAutoFlush(forceAfterReconnect = false): boolean {
    if (!this.autoFlush) {
      return false;
    }
    return this.canAttemptMutation(forceAfterReconnect);
  }

  private canAttemptMutation(forceAfterReconnect = false): boolean {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
    return forceAfterReconnect || !isBackendKnownUnavailable();
  }

  private registerFlushTriggers(): void {
    const refreshAndFlush = (forceAfterReconnect = false) => {
      this.refreshFailedMutations().catch(() => {});
      this.refreshPendingMutations().catch(() => {});
      this.scheduleFlush(forceAfterReconnect);
    };

    let accountSubscription: { unsubscribe(): void } | undefined;
    const bindAccountSubscription = () => {
      accountSubscription?.unsubscribe();
      accountSubscription =
        this.workspaceServerService.server?.account$.subscribe(() =>
          refreshAndFlush()
        );
      refreshAndFlush();
    };
    const serverSubscription = this.workspaceServerService.server$.subscribe(
      bindAccountSubscription
    );
    bindAccountSubscription();
    const unsubscribeBackendAvailability = onBackendAvailable(() =>
      refreshAndFlush(true)
    );
    this.disposables.push(() => serverSubscription.unsubscribe());
    this.disposables.push(() => accountSubscription?.unsubscribe());
    this.disposables.push(unsubscribeBackendAvailability);

    if (typeof window === 'undefined') {
      return;
    }

    const flushAfterReconnect = () => refreshAndFlush(true);
    const flushWhenVisible = () => refreshAndFlush();
    window.addEventListener('online', flushAfterReconnect);
    document.addEventListener('visibilitychange', flushWhenVisible);
    this.disposables.push(() => {
      window.removeEventListener('online', flushAfterReconnect);
      document.removeEventListener('visibilitychange', flushWhenVisible);
    });
  }
}

function compareMutations(
  left: OfflineMutationRecord,
  right: OfflineMutationRecord
): number {
  return (
    left.createdAt - right.createdAt ||
    (left.sequence ?? 0) - (right.sequence ?? 0) ||
    left.id.localeCompare(right.id)
  );
}

function cloneMutation<Operation extends OfflineMutationOperation>(
  mutation: OfflineMutationRecord<Operation>
): OfflineMutationRecord<Operation> {
  return {
    ...mutation,
    payload: { ...mutation.payload },
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  return 'Unknown mutation error';
}

function isTransientMutationError(err: unknown): boolean {
  if (err instanceof OfflineMutationTransientError) {
    return true;
  }
  if (err instanceof OfflineMutationDomainError) {
    return false;
  }

  const status = errorStatus(err);
  if (status === 408 || status === 425 || status === 429) {
    return true;
  }
  if (status >= 400 && status < 500) {
    return false;
  }
  if (isDomainErrorCode(err)) {
    return false;
  }

  const message = errorMessage(err).toLowerCase();
  if (
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('denied') ||
    message.includes('unauthorized') ||
    message.includes('validation') ||
    message.includes('not found')
  ) {
    return false;
  }

  return true;
}

function errorStatus(err: unknown): number {
  if (!err || typeof err !== 'object') {
    return 0;
  }
  const status = (err as { status?: unknown }).status;
  if (typeof status === 'number') {
    return status;
  }
  const statusCode = (err as { statusCode?: unknown }).statusCode;
  if (typeof statusCode === 'number') {
    return statusCode;
  }
  const originalResponse = (
    err as {
      originalResponse?: { getStatus?: () => unknown };
    }
  ).originalResponse;
  const responseStatus = originalResponse?.getStatus?.();
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }
  return 0;
}

function isPendingDuplicate(
  candidate: OfflineMutationRecord,
  record: OfflineMutationRecord
): boolean {
  return (
    candidate.scopeKey === record.scopeKey &&
    candidate.idempotencyKey === record.idempotencyKey &&
    (candidate.status === 'pending' || candidate.status === 'flushing')
  );
}

/**
 * The record a claim writes, the unchanged record when it is already flushing
 * (sent again after a reload), or null when it may not be sent now.
 */
function claimedMutation(
  record: OfflineMutationRecord,
  now: number
): OfflineMutationRecord | null {
  if (record.status === 'flushing') {
    return record;
  }
  if (
    record.status === 'failed' ||
    (record.claimableAt !== undefined && record.claimableAt > now)
  ) {
    return null;
  }
  return {
    ...record,
    status: 'flushing',
    updatedAt: now,
    sentAt: record.sentAt ?? now,
  };
}

function isUnsentMutation(record: OfflineMutationRecord): boolean {
  return record.status === 'pending' && record.sentAt === undefined;
}

function dedupedMutationId(
  scope: OfflineMutationScope,
  idempotencyKey: string
): string {
  return `dedupe:${offlineMutationScopeKey(scope)}:${idempotencyKey}`;
}

function isDomainErrorCode(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const values = [
    (err as { code?: unknown }).code,
    (err as { type?: unknown }).type,
    (err as { name?: unknown }).name,
  ];
  return values.some(
    value =>
      typeof value === 'string' &&
      ['BAD_REQUEST', 'GRAPHQL_BAD_REQUEST'].includes(value.toUpperCase())
  );
}
